// Shared period labels for the period_over_period summary views.
//
// Both the stats table and the comparison chart describe the two periods the
// same way: the CURRENT period spelled out concretely, and the PRIOR period
// as a relative phrase ("previous week/month/year") — never the bare words
// "current"/"prior".
//
//   granularity   current                       prior
//   W             "week starting May 24"        "previous week"
//   M             "May" (+ year cross-year)     "Apr" (+ year cross-year)
//   Y             "2026"                        "2025"
//
// Months and years have natural names, so both columns show them. Weeks don't,
// so the current week is spelled out by its start date and the prior is the
// relative "previous week". The year is appended to a month/week label only
// when the two periods straddle different calendar years.

import type { Granularity } from '../../charts/windowRange'

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

export interface PeriodBounds {
  start: string
  end: string
}

function parseIso(iso: string): { y: number; m: number; d: number } {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  return { y: y || 0, m: m || 1, d: d || 1 }
}

export function periodLabels(
  current: PeriodBounds,
  prior: PeriodBounds,
  granularity: Granularity,
): { current: string; prior: string } {
  const cs = parseIso(current.start)
  const ps = parseIso(prior.start)
  const showYear = cs.y !== ps.y

  // Month name (+ year when the comparison crosses a year boundary).
  const month = (p: { y: number; m: number }) =>
    showYear ? `${MONTHS[p.m - 1]} ${p.y}` : MONTHS[p.m - 1]

  if (granularity === 'Y') {
    return { current: String(cs.y), prior: String(ps.y) }
  }
  if (granularity === 'W') {
    return {
      current: `week starting ${MONTHS[cs.m - 1]} ${cs.d}${showYear ? `, ${cs.y}` : ''}`,
      prior: 'previous week',
    }
  }
  // Month (and any non-W/Y granularity) — both columns show the month name.
  return { current: month(cs), prior: month(ps) }
}
